import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import api from "../Services/api";
import { API_BASE_URL } from "../config";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);
export { api };


export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [navigation, setNavigation] = useState([]);
    const [onboardingComplete, setOnboardingComplete] = useState(true);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const fetchNavigation = async () => {
        try {
            const response = await api.get("organizations/my-navigation/");
            const { modules, onboarding_complete } = response.data;
            setNavigation(modules);
            setOnboardingComplete(onboarding_complete);
            localStorage.setItem("navigation", JSON.stringify(modules));
            localStorage.setItem("onboardingComplete", JSON.stringify(onboarding_complete));
        } catch (error) {
            console.error("Failed to fetch navigation", error);
        }
    };

    const fetchUser = async () => {
        try {
            const response = await api.get("users/me/");
            setUser(response.data);
            localStorage.setItem("user", JSON.stringify(response.data));
        } catch (error) {
            console.error("Failed to fetch user info", error);
        }
    };

    const refreshUser = async () => {
        await Promise.all([fetchUser(), fetchNavigation()]);
    };

    useEffect(() => {
        const initAuth = async () => {
            const storedToken = localStorage.getItem("accessToken");
            const storedUser = localStorage.getItem("user");
            const storedNav = localStorage.getItem("navigation");
            const storedOnboarding = localStorage.getItem("onboardingComplete");

            if (storedToken && storedUser) {
                api.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
                setUser(JSON.parse(storedUser));
                setIsAuthenticated(true);
                if (storedNav) {
                    setNavigation(JSON.parse(storedNav));
                }
                if (storedOnboarding !== null) {
                    setOnboardingComplete(JSON.parse(storedOnboarding));
                }
                // Wait for fresh data before showing the app
                await Promise.all([fetchNavigation(), fetchUser()]);
            }
            setLoading(false);
        };

        initAuth();

        const handleRbacUpdate = () => {
            console.log("RBAC update detected, syncing permissions...");
            fetchNavigation();
            fetchUser();
        };
        window.addEventListener("rbac_update", handleRbacUpdate);
        return () => window.removeEventListener("rbac_update", handleRbacUpdate);
    }, []);

    const login = async (username, password) => {
        try {
            const response = await axios.post(`${API_BASE_URL}users/login/`, { username, password });
            const { access, refresh, user: userData } = response.data;

            localStorage.setItem("accessToken", access);
            localStorage.setItem("refreshToken", refresh);
            localStorage.setItem("isAuth", "true");
            api.defaults.headers.common["Authorization"] = `Bearer ${access}`;
            
            // Set access token cookie for secure WebSocket and HTTP authentication
            document.cookie = `access_token=${access}; path=/; max-age=86400; SameSite=Lax`;

            // Fetch navigation and full user details in parallel using fresh token headers
            const [navResponse, userResponse] = await Promise.all([
                axios.get(`${API_BASE_URL}organizations/my-navigation/`, {
                    headers: { Authorization: `Bearer ${access}` }
                }),
                axios.get(`${API_BASE_URL}users/me/`, {
                    headers: { Authorization: `Bearer ${access}` }
                })
            ]);

            const { modules, onboarding_complete } = navResponse.data;
            const fullUserData = userResponse.data;

            localStorage.setItem("user", JSON.stringify(fullUserData));
            localStorage.setItem("navigation", JSON.stringify(modules));
            localStorage.setItem("onboardingComplete", JSON.stringify(onboarding_complete));

            // Update states atomically to prevent half-authenticated rendering race conditions
            setNavigation(modules);
            setOnboardingComplete(onboarding_complete);
            setUser(fullUserData);
            setIsAuthenticated(true);

            return { success: true };
        } catch (error) {
            console.error("Login error", error);
            return { 
                success: false, 
                message: error.response?.data?.detail || "Invalid credentials or server error." 
            };
        }
    };

    const logout = useCallback(() => {
        delete api.defaults.headers.common["Authorization"];
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        localStorage.removeItem("isAuth");
        localStorage.removeItem("navigation");
        localStorage.removeItem("onboardingComplete");
        
        // Remove access token cookie
        document.cookie = "access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";

        setUser(null);
        setNavigation([]);
        setIsAuthenticated(false);
        setOnboardingComplete(true);
    }, []);

    const register = async (userData) => {
        try {
            const response = await api.post("users/register/", userData);
            return { success: true, data: response.data };
        } catch (error) {
            console.error("Registration error", error);
            return { 
                success: false, 
                message: error.response?.data || "Registration failed." 
            };
        }
    };

    return (
        <AuthContext.Provider value={{ user, navigation, loading, isAuthenticated, onboardingComplete, setOnboardingComplete, login, logout, register, refreshUser }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
